import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { fetchCampaignTable } from 'features/dashboard/table/campaign/campaignTableSlice'
import { DashboardInfoTypes, DashboardTypes } from 'features/Codes'
import { fetchAdGroupTable } from 'features/dashboard/table/adGroup/adGroupTableSlice'
import { fetchKeywordTable } from 'features/dashboard/table/keyword/keywordTableSlice'
import { fetchCreativeTable } from 'features/dashboard/table/creative/creativeTableSlice'
import api from 'api/axios/foka'
import { fetchTreeCampaigns } from 'features/dashboard/tree/dashboardTreeSlice'
import {
  fetchDashboardInfoAccount,
  fetchDashboardInfoAdGroup,
  fetchDashboardInfoCampaign,
  fetchDashboardInfoCreative,
  fetchDashboardInfoKeyword,
  fetchDashboardInfoTotalAdGroup,
  fetchDashboardInfoTotalCampaign,
  fetchDashboardInfoTotalCreative,
  fetchDashboardInfoTotalKeyword,
  fetchDashboardInfoWalletSpend,
} from 'features/dashboard/info/dashboardInfoSlice'
import { CALENDAR_PRESETS, fmt } from 'components/calendar/calendarUtil'
import moment from 'moment'
import { fetchChart } from 'features/dashboard/info/chart/dashboardChartSlice'

const initialState = {
  type: DashboardTypes.CAMPAIGN,
  isRefreshing: false,
  isLoading: true,
  isHeavyUser: false,
  filterEnabled: true,
  lastUpdatedTimestamp: Date.now(),
  preset: CALENDAR_PRESETS.LAST_7_DAYS,
  from: moment().subtract(6, 'day').format(fmt),
  to: moment().format(fmt),
}

const DashboardTableFetchers = {
  [DashboardTypes.CAMPAIGN]: fetchCampaignTable,
  [DashboardTypes.AD_GROUP]: fetchAdGroupTable,
  [DashboardTypes.KEYWORD]: fetchKeywordTable,
  [DashboardTypes.CREATIVE]: fetchCreativeTable,
}

const DashboardInfoFetchers = {
  [DashboardInfoTypes.AD_ACCOUNT]: fetchDashboardInfoAccount,
  [DashboardInfoTypes.CAMPAIGN]: fetchDashboardInfoCampaign,
  [DashboardInfoTypes.AD_GROUP]: fetchDashboardInfoAdGroup,
  [DashboardInfoTypes.KEYWORD]: fetchDashboardInfoKeyword,
  [DashboardInfoTypes.CREATIVE]: fetchDashboardInfoCreative,
  [DashboardInfoTypes.TOTAL_CAMPAIGN]: fetchDashboardInfoTotalCampaign,
  [DashboardInfoTypes.TOTAL_AD_GROUP]: fetchDashboardInfoTotalAdGroup,
  [DashboardInfoTypes.TOTAL_KEYWORD]: fetchDashboardInfoTotalKeyword,
  [DashboardInfoTypes.TOTAL_CONTENT_MATCHING]: fetchDashboardInfoWalletSpend,
  [DashboardInfoTypes.TOTAL_CREATIVE]: fetchDashboardInfoTotalCreative,
}

export const initialize = createAsyncThunk('dashboard/initialize', async () => {
  const [isHeavyUser, filterEnabled] = await Promise.all([
    api.dashboard
      .getRestrictionAccount()
      .then(() => true)
      .catch(() => false),
    api.dashboard
      .getFilterFeature()
      .then(response => response?.data?.result)
      .catch(() => true),
  ])
  return { isHeavyUser, filterEnabled }
})

export const refreshDashboardTree = createAsyncThunk(
  'dashboard/refreshDashboardTree',
  async (noArgs, { getState, dispatch }) => {
    const { visible } = getState().dashboard.tree
    if (visible) {
      await dispatch(fetchTreeCampaigns())
    }
  }
)

export const refreshDashboardTable = createAsyncThunk(
  'dashboard/refreshDashboardTable',
  async (noArgs, { getState, dispatch }) => {
    const { type } = getState().dashboard.common
    await dispatch(DashboardTableFetchers[type]())
  }
)

export const refreshDashboardInfo = createAsyncThunk(
  'dashboard/refreshDashboardInfo',
  async (noArgs, { getState, dispatch }) => {
    const { type, id, visible, tabs } = getState().dashboard.info
    const tab = tabs[type]
    const fetcher =
      tab === 'priceInfoTab'
        ? fetchDashboardInfoWalletSpend
        : DashboardInfoFetchers[type]
    if (visible && fetcher) {
      await dispatch(fetcher({ id }))
    }
  }
)

export const refreshDashboardChart = createAsyncThunk(
  'dashboard/refreshDashboardChart',
  async (noArgs, { getState, dispatch }) => {
    const { visible } = getState().dashboard.info
    if (visible) {
      await dispatch(fetchChart())
    }
  }
)

export const refreshDashboard = createAsyncThunk(
  'dashboard/refreshDashboard',
  async (noArgs, { dispatch }) => {
    await Promise.all([
      dispatch(refreshDashboardTree()),
      dispatch(refreshDashboardTable()),
      dispatch(refreshDashboardInfo()),
      dispatch(refreshDashboardChart()),
    ])
    return Date.now()
  }
)

const dashboardCommonSlice = createSlice({
  name: 'dashboard/common',
  initialState,

  reducers: {
    reset: () => initialState,
    setPeriod: (state, { payload: { preset, from, to } }) => {
      state.preset = preset
      state.from = from
      state.to = to
    },
    setType: (state, { payload: type }) => {
      state.type = type
    },
    setInfo: (state, { payload: info }) => {
      state.info.type = info.type
      state.info.id = info.id
      state.info.infos = info.infos
    },
  },

  extraReducers: builder => {
    builder.addCase(refreshDashboard.pending, state => {
      state.isRefreshing = true
    })

    builder.addCase(refreshDashboard.rejected, state => {
      state.isRefreshing = false
    })

    builder.addCase(refreshDashboard.fulfilled, (state, { payload }) => {
      state.lastUpdatedTimestamp = payload
      state.isRefreshing = false
    })

    builder.addCase(initialize.pending, state => {
      state.isLoading = true
    })

    builder.addCase(initialize.rejected, state => {
      state.isLoading = false
    })

    builder.addCase(
      initialize.fulfilled,
      (state, { payload: { isHeavyUser, filterEnabled } }) => {
        state.isHeavyUser = isHeavyUser
        state.filterEnabled = filterEnabled
        state.isLoading = false
      }
    )
  },
})
export const { reset, setPeriod, setType } = dashboardCommonSlice.actions
export default dashboardCommonSlice.reducer
